import React, { useState } from "react";
import {
  sendClientSideSplitTrackEvent,
  useClientSideSplitTreatments,
} from "@leafly-com/split-next";
import dynamic from "next/dynamic";

import useBrowserStorage from "hooks/useBrowserStorage";
import { getSplitKey } from "utils/split/getSplitKey";

import ChatIcon from "../Icons/chat.svg";
import X from "../Icons/x.svg";

const TWENTY_FOUR_HOURS = 1000 * 60 * 60 * 24;

const LeafbotChat = dynamic(() => import("components/LeafbotChat/LeafbotChat"));

const LeafbotButton = () => {
  const [tooltipSeenAt, setTooltipSeenAt] = useBrowserStorage<Date | null>(
    "local",
    "buddee_chat_seen_at",
    null
  );

  /**
   * Show tooltip if it hasn't been seen in the last 24 hours as we don't want to
   * annoy users yet we want to make sure they know about it.
   */
  const [isTooltipVisible, setIsTooltipVisible] = useState(
    !tooltipSeenAt ||
      new Date().getTime() - new Date(tooltipSeenAt).getTime() >
        TWENTY_FOUR_HOURS
  );

  const [isChatVisible, setIsChatVisible] = useState(false);

  const split = useClientSideSplitTreatments(
    ["leafbotChat_flag"],
    getSplitKey()
  );

  const dismissToolip = () => {
    setTooltipSeenAt(new Date());
    setIsTooltipVisible(false);
  };

  const openChat = () => {
    setIsChatVisible(true);
    setIsTooltipVisible(false);
    sendClientSideSplitTrackEvent(
      "webWeb_leafbotChatClicked_frontend",
      getSplitKey()
    );
  };

  if (split.isLoading || split.treatments.leafbotChat_flag.treatment !== "on") {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 h-90 z-140">
      {isTooltipVisible && (
        <div className="absolute w-48 bg-white elevation-low rounded bottom-16 left-0 p-2 text-xs">
          <div>
            Introducing the new Leafly Leafbot! Let me know if I can help you
            find what you’re looking for.
          </div>

          <button
            onClick={dismissToolip}
            className="text-lg border-none bg-transparent cursor-pointer float-right"
            aria-label="Close"
            data-testid="leafbot-tooltip-close-button"
          >
            <X width={22} height={22} />
          </button>
        </div>
      )}

      {isChatVisible && (
        <div className="absolute bottom-16 w-full max-w-[480px]">
          <button
            onClick={() => setIsChatVisible(false)}
            className="cursor-pointer absolute top-[11px] right-[11px]"
            aria-label="Close chat"
            data-testid="leafbot-chat-close-button"
          >
            <X width={22} height={22} />
          </button>
          <LeafbotChat />
        </div>
      )}

      <button
        className="w-12 h-12 bg-blue-500 rounded-full absolute bottom-0 flex items-center justify-center"
        onClick={() => openChat()}
        aria-label="Open Chat"
        data-testid="leafbot-button"
      >
        <ChatIcon className="w-16 h-16" />
      </button>
    </div>
  );
};

export default LeafbotButton;
